import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Clock, MapPin, LayoutGrid, CheckCircle2, AlertTriangle } from 'lucide-react';
import useIsMobile from '../utils/useIsMobile';

// Calendario condiviso senza login (25/10/2026): /calendario-pubblico/<token>, sola lettura.
// Cosa si vede lo decide il link (Calendario attività → Link condivisi). Il server non manda mai altro.
// Dal link personale di un destinatario (26/10): in cima le modifiche da quando ha visto l'ultima volta
// (nuovi, modificati con prima → dopo, annullati) e il bottone "Ho visto", che avvisa lo staff a video.
const BASE = process.env.REACT_APP_API_URL || '/api';
const pubblico = axios.create({ baseURL: BASE });
const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const GIORNI = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const maiuscola = (t) => t[0].toUpperCase() + t.slice(1);
const giornoLungo = (s) => maiuscola(new Date(s + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }));
const giornoBreve = (s) => new Date(s + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
const quando = (d) => new Date(d).toLocaleString('it-IT', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
const fine = (e) => e.data_fine || e.data;
const orario = (e) => e.ora_inizio ? (e.ora_fine ? `${e.ora_inizio} – ${e.ora_fine}` : `dalle ${e.ora_inizio}`) : 'Tutto il giorno';
const campi = (n) => n ? `${n} ${n === 1 ? 'campo' : 'campi'}` : null;
const colore = (e) => e.colore || '#C24B08';
// valore di una modifica in parole
const testo = (campo, v) => v === null || v === '' ? '—' : (campo === 'Giorno' || campo === 'Fino al') ? giornoBreve(v) : campo === 'Campi' ? campi(v) : String(v);

export default function CalendarioPubblico() {
  const { token } = useParams();
  const mobile = useIsMobile();
  const oggi = iso(new Date());
  const [vista, setVista] = useState('elenco');
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [mese, setMese] = useState(new Date().getMonth());
  const [dati, setDati] = useState(null);
  const [err, setErr] = useState('');
  const [giornoSel, setGiornoSel] = useState(oggi);

  const celle = useMemo(() => {
    const primo = new Date(anno, mese, 1);
    const start = new Date(anno, mese, 1 - ((primo.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [anno, mese]);

  const carica = useCallback(() => {
    const q = vista === 'mese' ? `?da=${iso(celle[0])}&a=${iso(celle[41])}` : '';
    return pubblico.get(`/calendario-pubblico/${encodeURIComponent(token)}${q}`)
      .then(r => { setDati(r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.error || 'Calendario non disponibile'));
  }, [token, vista, celle]);
  useEffect(() => { carica(); }, [carica]);

  const intestazione = dati?.sedi?.length ? `Occupazione campi — ${dati.sedi.join(', ')}` : dati?.nome;
  useEffect(() => { if (dati) document.title = `${intestazione} · ${dati.societa}`; }, [dati, intestazione]);

  const eventi = dati?.eventi || [];
  const cambiati = useMemo(() => {
    const m = {};
    for (const x of dati?.visione?.modifiche || []) if (x.tipo !== 'ANNULLATO') m[x.id] = x.tipo;
    return m;
  }, [dati]);
  const delGiorno = (g) => eventi.filter(e => e.data <= g && fine(e) >= g).sort((a, b) => (a.ora_inizio || '').localeCompare(b.ora_inizio || ''));
  const prossimi = useMemo(() => eventi.filter(e => fine(e) >= oggi), [eventi, oggi]);
  const cambiaMese = (d) => {
    let m = mese + d, a = anno;
    if (m < 0) { m = 11; a--; } else if (m > 11) { m = 0; a++; }
    setMese(m); setAnno(a);
  };

  if (err) return <div style={s.pagina}><div style={s.contenitore}><div style={s.box}><b>{err}</b><div style={s.dim}>Il link potrebbe essere stato disattivato. Chiedi a chi te l'ha mandato.</div></div></div></div>;
  if (!dati) return <div style={s.pagina}><div style={s.contenitore}><div style={s.dim}>Carico il calendario…</div></div></div>;

  return (
    <div style={s.pagina}>
      <div style={s.contenitore}>
        <div style={s.societa}>{dati.societa}</div>
        <h1 style={s.titolo}>{intestazione}</h1>
        <div style={s.spiega}>
          {dati.sedi?.length
            ? <>Qui trovi i giorni e gli orari in cui <b>{dati.societa}</b> utilizza i campi{dati.sedi.length === 1 ? ` di ${dati.sedi[0]}` : ''}, con il numero di campi occupati. La pagina è sempre aggiornata.</>
            : <>Calendario delle attività di <b>{dati.societa}</b>. La pagina è sempre aggiornata.</>}
        </div>

        {dati.visione && <PresaVisione token={token} visione={dati.visione} onFatto={carica} />}

        <div style={s.schede}>
          {[['elenco', 'Prossime date'], ['mese', 'Mese']].map(([k, l]) =>
            <button key={k} style={{ ...s.scheda, ...(vista === k ? s.schedaOn : {}) }} onClick={() => setVista(k)}>{l}</button>)}
        </div>

        {vista === 'elenco' ? (
          <div style={s.box}>
            {prossimi.length === 0 && <div style={s.dim}>Nessuna data in programma.</div>}
            {prossimi.map(e => <Riga key={e.id} e={e} stato={cambiati[e.id]} mobile={mobile} oggi={oggi} />)}
          </div>
        ) : (
          <div style={s.box}>
            <div style={s.nav}>
              <button style={s.icona} onClick={() => cambiaMese(-1)} aria-label="Mese prima"><ChevronLeft size={20} /></button>
              <div style={s.mese}>{MESI[mese]} {anno}</div>
              <button style={s.icona} onClick={() => cambiaMese(1)} aria-label="Mese dopo"><ChevronRight size={20} /></button>
            </div>
            <div style={s.griglia7}>{GIORNI.map(g => <div key={g} style={s.testaGiorno}>{g}</div>)}</div>
            <div style={s.griglia7}>
              {celle.map(d => {
                const g = iso(d), evs = delGiorno(g);
                return (
                  <button key={g} onClick={() => setGiornoSel(g)}
                    style={{ ...s.cella, minHeight: mobile ? 48 : 84, opacity: d.getMonth() !== mese ? 0.4 : 1, ...(g === giornoSel ? s.cellaSel : {}) }}>
                    <span style={{ ...s.numero, ...(g === oggi ? s.numeroOggi : {}) }}>{d.getDate()}</span>
                    {mobile
                      ? <span style={s.puntini}>{evs.slice(0, 4).map(e => <span key={e.id} style={{ ...s.puntino, background: colore(e) }} />)}</span>
                      : evs.slice(0, 3).map(e => <span key={e.id} style={{ ...s.chip, borderLeftColor: colore(e), background: colore(e) + '18' }}>{e.ora_inizio && <b>{e.ora_inizio} </b>}{e.campi ? `${e.campi}c · ` : ''}{e.titolo}</span>)}
                    {!mobile && evs.length > 3 && <span style={s.dim}>+{evs.length - 3}</span>}
                  </button>
                );
              })}
            </div>
            <div style={{ ...s.giorno, marginTop: 14 }}>{giornoLungo(giornoSel)}</div>
            {delGiorno(giornoSel).length === 0 ? <div style={s.dim}>Nessun evento.</div> : delGiorno(giornoSel).map(e => <Riga key={e.id} e={e} stato={cambiati[e.id]} mobile={mobile} oggi={oggi} />)}
          </div>
        )}
        <div style={{ ...s.dim, textAlign: 'center', marginTop: 18 }}>Pagina in sola lettura, sempre aggiornata.</div>
      </div>
    </div>
  );
}

// Una data: giorno in evidenza a sinistra, poi orario, campi, evento
function Riga({ e, stato, mobile, oggi }) {
  const piuGiorni = e.data_fine && e.data_fine !== e.data;
  return (
    <div style={{ ...s.riga, flexDirection: mobile ? 'column' : 'row', ...(stato ? s.rigaCambiata : {}) }}>
      <div style={{ ...s.data, ...(mobile ? { minWidth: 0 } : {}), ...(e.data <= oggi && fine(e) >= oggi ? { color: '#C24B08' } : {}) }}>
        {giornoBreve(e.data)}{piuGiorni && <> → {giornoBreve(e.data_fine)}</>}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={s.rigaInfo}>
          <span style={s.forte}><Clock size={14} /> {orario(e)}</span>
          {e.campi && <span style={s.campi}><LayoutGrid size={14} /> {campi(e.campi)}</span>}
          {stato && <span style={{ ...s.badge, ...(stato === 'NUOVO' ? s.badgeNuovo : s.badgeMod) }}>{stato === 'NUOVO' ? 'Nuovo' : 'Modificato'}</span>}
        </div>
        <div style={{ fontWeight: 600 }}>{e.titolo}</div>
        <div style={s.rigaInfo}>
          {e.luogo && <span style={s.voce}><MapPin size={13} /> {e.luogo.nome}{e.luogo.indirizzo ? ` · ${e.luogo.indirizzo}` : ''}</span>}
          {e.tags.map(t => <span key={t} style={s.tag}>{t}</span>)}
        </div>
        {e.note && <div style={{ ...s.dim, whiteSpace: 'pre-line' }}>{e.note}</div>}
      </div>
    </div>
  );
}

// Riquadro del link personale: prima volta / modifiche da vedere / tutto visto
function PresaVisione({ token, visione, onFatto }) {
  const [invio, setInvio] = useState(false);
  const [err, setErr] = useState('');
  const mod = visione.modifiche || [];
  const daConfermare = !visione.visto_il || mod.length > 0;

  const conferma = async () => {
    setInvio(true); setErr('');
    try {
      await pubblico.post(`/calendario-pubblico/${encodeURIComponent(token)}/visione`, { versione: visione.versione });
      await onFatto();
    } catch (er) { setErr(er.response?.data?.error || 'Non registrato, riprova'); }
    finally { setInvio(false); }
  };

  if (!daConfermare) return (
    <div style={{ ...s.visione, ...s.visioneOk }}>
      <CheckCircle2 size={20} style={{ flex: '0 0 auto' }} />
      <div><b>Grazie {visione.nome}</b>: nessuna modifica da quando hai visto, il {quando(visione.visto_il)}.</div>
    </div>
  );
  return (
    <div style={{ ...s.visione, ...s.visioneAttesa, flexDirection: 'column', alignItems: 'stretch' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <AlertTriangle size={20} style={{ flex: '0 0 auto', marginTop: 1 }} />
        <div>
          {!visione.visto_il
            ? <>Ciao <b>{visione.nome}</b>, controlla il calendario qui sotto e tocca <b>«Ho visto»</b> per farcelo sapere.</>
            : <>Ciao <b>{visione.nome}</b>, ci sono <b>{mod.length} {mod.length === 1 ? 'modifica' : 'modifiche'}</b> da quando hai visto l'ultima volta ({quando(visione.visto_il)}). Controllale e tocca <b>«Ho visto»</b>.</>}
        </div>
      </div>
      {mod.length > 0 && (
        <div style={s.listaMod}>
          {mod.map(m => (
            <div key={m.tipo + m.id} style={s.mod}>
              <div style={s.rigaInfo}>
                <span style={{ ...s.badge, ...(m.tipo === 'NUOVO' ? s.badgeNuovo : m.tipo === 'ANNULLATO' ? s.badgeAnnullato : s.badgeMod) }}>{m.tipo === 'NUOVO' ? 'Nuovo' : m.tipo === 'ANNULLATO' ? 'Annullato' : 'Modificato'}</span>
                <b style={m.tipo === 'ANNULLATO' ? { textDecoration: 'line-through' } : {}}>{giornoBreve(m.data)} · {m.titolo}</b>
              </div>
              {m.tipo === 'ANNULLATO' && <div style={s.dim}>Era {m.ora_inizio ? `${m.ora_inizio}${m.ora_fine ? ` – ${m.ora_fine}` : ''}` : 'tutto il giorno'}{m.campi ? `, ${campi(m.campi)}` : ''}: non occuperemo più i campi.</div>}
              {m.cambi?.map(c => <div key={c.campo} style={s.dim}>{c.campo}: <s>{testo(c.campo, c.prima)}</s> → <b style={{ color: 'var(--text)' }}>{testo(c.campo, c.dopo)}</b></div>)}
            </div>
          ))}
        </div>
      )}
      {err && <div style={s.err}>{err}</div>}
      <div><button type="button" style={s.btnPrimario} disabled={invio} onClick={conferma}><CheckCircle2 size={16} /> {invio ? 'Registro…' : 'Ho visto'}</button></div>
    </div>
  );
}

const s = {
  pagina: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', padding: '24px 16px 40px', boxSizing: 'border-box' },
  contenitore: { maxWidth: 880, margin: '0 auto' },
  societa: { fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', color: '#C24B08', textTransform: 'uppercase' },
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 34, fontWeight: 800, letterSpacing: '1px', margin: '2px 0 6px', lineHeight: 1.1 },
  spiega: { color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.5, marginBottom: 16, maxWidth: 680 },
  visione: { display: 'flex', gap: 10, alignItems: 'center', borderRadius: 16, padding: 14, marginBottom: 16, fontSize: 14, lineHeight: 1.45 },
  visioneOk: { background: '#E7F6EC', color: '#1F5E39', border: '1px solid #BFE5CC' },
  visioneAttesa: { background: '#FFF6EC', color: '#5C2A00', border: '1px solid #FBD5A8', gap: 12 },
  listaMod: { display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-card)', color: 'var(--text)', borderRadius: 12, padding: 12 },
  mod: { display: 'flex', flexDirection: 'column', gap: 3, paddingBottom: 8, borderBottom: '1px solid var(--border)' },
  schede: { display: 'inline-flex', gap: 4, background: 'var(--bg-input)', padding: 4, borderRadius: 12, marginBottom: 14 },
  scheda: { border: 'none', background: 'transparent', padding: '8px 14px', borderRadius: 9, fontWeight: 600, fontSize: 14, color: 'var(--text-muted)', cursor: 'pointer' },
  schedaOn: { background: 'var(--bg-card)', color: 'var(--text)', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,.08)' },
  box: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  riga: { display: 'flex', gap: 14, padding: '12px 14px', borderRadius: 12, background: 'var(--bg)', border: '1px solid transparent' },
  rigaCambiata: { borderColor: '#FBD5A8', background: '#FFF8F0' },
  data: { minWidth: 120, fontWeight: 800, fontSize: 15, textTransform: 'capitalize' },
  rigaInfo: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  forte: { display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: 14 },
  campi: { display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 700, fontSize: 13, color: '#1D4FA8', background: '#E6EEFB', borderRadius: 8, padding: '3px 9px' },
  voce: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-muted)' },
  tag: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, color: '#8A3B00', background: '#FFF1E0' },
  badge: { fontSize: 11, fontWeight: 800, padding: '3px 8px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  badgeNuovo: { color: '#1F7A45', background: '#E7F6EC' },
  badgeMod: { color: '#8A3B00', background: '#FFE4C4' },
  badgeAnnullato: { color: '#9B1C1C', background: '#FDE8E8' },
  giorno: { fontWeight: 800, fontSize: 15 },
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 },
  mese: { fontWeight: 800, fontSize: 18, minWidth: 160, textAlign: 'center' },
  griglia7: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 },
  testaGiorno: { textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', padding: '4px 0' },
  cella: { border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)', padding: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch', cursor: 'pointer', textAlign: 'left', minWidth: 0, overflow: 'hidden', color: 'var(--text)' },
  cellaSel: { borderColor: '#C24B08', boxShadow: '0 0 0 1px #C24B08 inset' },
  numero: { fontSize: 12, fontWeight: 700, alignSelf: 'flex-start', minWidth: 22, height: 22, lineHeight: '22px', textAlign: 'center', borderRadius: 11 },
  numeroOggi: { background: '#C24B08', color: '#fff' },
  chip: { fontSize: 11, lineHeight: 1.3, borderLeft: '3px solid', borderRadius: 4, padding: '1px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  puntini: { display: 'flex', gap: 3, flexWrap: 'wrap', paddingLeft: 2 },
  puntino: { width: 7, height: 7, borderRadius: 4 },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 16px', borderRadius: 999, border: '1px solid #C24B08', background: '#C24B08', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', minHeight: 42 },
  icona: { border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, display: 'inline-flex', borderRadius: 8 },
  err: { background: '#FDE8E8', color: '#9B1C1C', borderRadius: 10, padding: '8px 10px', fontSize: 14 },
  dim: { color: 'var(--text-muted)', fontSize: 13 },
};
